import React from 'react';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import Header from './Header.jsx';
import {NoAccessCard} from './PageStates.jsx';

// Component-level guard that redirects non-admin (or non-authenticated) users
// off a sensitive route to a fallback view. Used by /tasks auth gating
// (TaskCenterView mounts under requireAdmin:false; the System Tasks tab
// inside self-gates to admins) and any other admin-only surfaces, so that
// a non-admin who types a sensitive URL doesn't see admin UI even
// momentarily — Header gating alone is not enough.
//
// authState shape (from AuthContext):
//   null             — still loading; render nothing, do not redirect.
//   false            — signed out; redirect to fallback (LoginScreen renders
//                      from there).
//   {user, role, ...}— authenticated; allow if role === 'admin' (when
//                      requireAdmin) or always (when requireAdmin=false).
//
// Redirect uses setView() — the same setter every other view-switch in the
// app uses — so back/forward + URL sync stays consistent with the rest of
// the router.
//
// Design Constitution N10 (PR9): a signed-in user without the role is no longer
// redirected. The URL stays and the page shows the header with 'No Access — ask
// an admin'; Go Back goes Home. Signed-out callers still go to the fallback,
// where the sign-in screen renders. Who may open the page is unchanged.

type UnauthorizedRedirectProps = {
  requireAdmin?: boolean;
  fallbackView?: string;
  children?: React.ReactNode;
};

// Read straight off the hook rather than imported as a named type: whatever
// AuthContext decides `authState` is, this gate is describing the same thing by
// construction and cannot drift from it.
type AuthStateValue = ReturnType<typeof useAuth>['authState'];

export default function UnauthorizedRedirect({
  requireAdmin = true,
  fallbackView = 'home',
  children,
}: UnauthorizedRedirectProps) {
  const {authState} = useAuth();
  const {setView} = useUI();
  const allowed = isAllowed(authState, requireAdmin);
  const signedIn = !!authState && typeof authState === 'object';

  React.useEffect(() => {
    if (authState === null) return; // still loading — wait
    if (!signedIn) {
      setView(fallbackView);
    }
  }, [authState, signedIn, setView, fallbackView]);

  if (authState === null) return null; // loading — render nothing
  if (!signedIn) return null; // signed out — redirecting to the sign-in screen
  if (!allowed)
    return (
      <div data-farm-no-access="1" style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
        <Header />
        <NoAccessCard onBack={() => setView('home')} />
      </div>
    );
  return <>{children}</>;
}

function isAllowed(authState: AuthStateValue, requireAdmin: boolean): boolean {
  if (authState === null) return false;
  if (authState === false) return false;
  if (!authState || typeof authState !== 'object') return false;
  if (requireAdmin && authState.role !== 'admin') return false;
  return true;
}
